import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Loader2, ArrowLeft, Mail, Key } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import AuthLayout from '../components/AuthLayout';
import { apiService } from '../services/apiService';
import { toast } from '../hooks/use-toast';

const PasswordResetPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // 'request' or 'confirm'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await apiService.requestPasswordReset(email);
      toast({
        title: "Code envoyé",
        description: "Si votre email existe, vous recevrez un code de réinitialisation sous peu."
      });
      setStep('confirm');
    } catch (error) {
      console.error('Password reset request error:', error);
      setError(error.response?.data?.detail || 'Erreur lors de l\'envoi du code');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      setLoading(false);
      return;
    }

    try {
      await apiService.confirmPasswordReset(email, code, newPassword);
      toast({
        title: "Succès",
        description: "Votre mot de passe a été réinitialisé avec succès !"
      });
      navigate('/connexion');
    } catch (error) {
      console.error('Password reset confirm error:', error);
      setError(error.response?.data?.detail || 'Code invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  return <AuthLayout title={step === 'request' ? <>Retrouvez<br /><em>votre espace.</em></> : <>Un nouveau<br /><em>départ.</em></>} description={step === 'request' ? 'Recevez par email un code pour réinitialiser votre mot de passe.' : `Saisissez le code envoyé à ${email}, puis choisissez votre nouveau mot de passe.`}>
    <p className="korean-auth-step">ÉTAPE {step === 'request' ? '01 / 02 — VOTRE EMAIL' : '02 / 02 — VOTRE CODE'}</p>
    {step === 'request' ? (<form onSubmit={handleRequestReset} className="space-y-4 sm:space-y-6">
                {error && (
                  <Alert variant="destructive" className="border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-red-800 text-sm">{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium text-sm sm:text-base">
                    Adresse email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email" autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    required
                    disabled={loading}
                    className="h-10 sm:h-12 border-gray-300 focus:border-amber-500 focus:ring-amber-500 rounded-lg text-sm sm:text-base"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full h-10 sm:h-12 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm sm:text-base"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    'Envoyer le code'
                  )}
                </Button>
              </form>) : (<form onSubmit={handleConfirmReset} className="space-y-4 sm:space-y-6">
                {error && (
                  <Alert variant="destructive" className="border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-red-800 text-sm">{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="code" className="text-gray-700 font-medium text-sm sm:text-base">
                    Code de réinitialisation
                  </Label>
                  <Input
                    id="code"
                    name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}"
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="123456"
                    required
                    disabled={loading}
                    maxLength={6}
                    className="h-10 sm:h-12 border-gray-300 focus:border-amber-500 focus:ring-amber-500 rounded-lg text-sm sm:text-base text-center font-mono text-lg"
                  />
                  <p className="text-xs text-gray-500 text-center">
                    Code reçu par email (valable 15 minutes)
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-gray-700 font-medium text-sm sm:text-base">
                    Nouveau mot de passe
                  </Label>
                  <Input
                    id="newPassword"
                    name="newPassword" autoComplete="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    disabled={loading}
                    minLength={6}
                    className="h-10 sm:h-12 border-gray-300 focus:border-amber-500 focus:ring-amber-500 rounded-lg text-sm sm:text-base"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-gray-700 font-medium text-sm sm:text-base">
                    Confirmer le mot de passe
                  </Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword" autoComplete="new-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    disabled={loading}
                    minLength={6}
                    className="h-10 sm:h-12 border-gray-300 focus:border-amber-500 focus:ring-amber-500 rounded-lg text-sm sm:text-base"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full h-10 sm:h-12 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm sm:text-base"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                      Réinitialisation...
                    </>
                  ) : (
                    'Réinitialiser le mot de passe'
                  )}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setStep('request')}
                    disabled={loading}
                    className="text-sm text-amber-600 hover:text-orange-700 underline disabled:opacity-50"
                  >
                    Renvoyer un nouveau code
                  </button>
                </div>
              </form>)}
    <div className="korean-auth-links"><Link to="/connexion">Retour à la connexion</Link></div>
  </AuthLayout>;
};
export default PasswordResetPage;
